/**
 * Fase 2A · P0-1 · EmpleadoAgenda
 * Agenda del centro para empleado (read-only + acciones básicas).
 * Backend: GET /api/appointments devuelve todas las citas del centro para empleado.
 * Muestra vista lista agrupada por día. Filtro toggle "Solo las mías" (operario_id=user.id).
 */
import { useEffect, useMemo, useState } from "react";
import { api, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  CalendarBlank, Clock, CarSimple, User as UserIcon, Funnel,
  CheckCircle, X as XIcon,
} from "@phosphor-icons/react";

function fmtDayHeader(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    const opts = { weekday: "long", day: "2-digit", month: "long" };
    const label = d.toLocaleDateString("es-ES", opts);
    if (d.toDateString() === now.toDateString()) return `Hoy · ${label}`;
    if (d.toDateString() === tomorrow.toDateString()) return `Mañana · ${label}`;
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch { return iso; }
}

function groupByDay(appts) {
  const map = new Map();
  for (const a of appts) {
    const day = new Date(a.date).toISOString().slice(0, 10);
    if (!map.has(day)) map.set(day, []);
    map.get(day).push(a);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, items]) => ({ day, items: items.sort((x, y) => new Date(x.date) - new Date(y.date)) }));
}

export default function EmpleadoAgenda() {
  const { user } = useAuth();
  const [appts, setAppts] = useState(null);
  const [onlyMine, setOnlyMine] = useState(false);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/appointments");
      setAppts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("No se pudieron cargar las citas");
      setAppts([]);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!appts) return [];
    let out = appts.filter((a) => a.status !== "Cancelado");
    if (onlyMine) out = out.filter((a) => a.operario_id === user?.id);
    return out;
  }, [appts, onlyMine, user?.id]);

  const groups = useMemo(() => groupByDay(filtered), [filtered]);

  const notifyReady = async (apptId) => {
    setUpdating(apptId);
    try {
      await api.post(`/appointments/${apptId}/notify-ready`);
      toast.success("Cliente notificado · vehículo listo");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo notificar");
    } finally { setUpdating(null); }
  };

  const cancelAppt = async (apptId) => {
    if (!window.confirm("¿Cancelar esta cita?")) return;
    setUpdating(apptId);
    try {
      await api.delete(`/appointments/${apptId}`);
      toast.success("Cita cancelada");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo cancelar la cita");
    } finally { setUpdating(null); }
  };

  if (appts === null) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 rounded-sm" />
        <Skeleton className="h-32 rounded-sm" />
        <Skeleton className="h-32 rounded-sm" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="empleado-agenda">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
            Agenda operativa
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-1">
            Citas del centro
          </h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2">
            Consulta y actualiza el estado de las citas del centro.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOnlyMine((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium border transition-colors ${
            onlyMine
              ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]"
              : "border-[var(--ag-border)] bg-transparent text-[var(--ag-text-dim)] hover:text-white"
          }`}
          data-testid="empleado-agenda-filter-mine"
        >
          <Funnel size={12} weight={onlyMine ? "fill" : "regular"} />
          {onlyMine ? "Mis citas" : "Todas"}
        </button>
      </header>

      {groups.length === 0 ? (
        <div className="ag-card p-8 text-center" data-testid="empleado-agenda-empty">
          <CalendarBlank size={32} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto" />
          <div className="text-sm text-[var(--ag-text-dim)] mt-3">
            {onlyMine ? "No tienes citas asignadas." : "No hay citas próximas en el centro."}
          </div>
          {onlyMine && (
            <button
              type="button"
              onClick={() => setOnlyMine(false)}
              className="text-xs text-[var(--ag-gold)] mt-3 hover:underline"
            >
              Ver todas las citas del centro
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(({ day, items }) => (
            <section key={day} data-testid={`empleado-agenda-day-${day}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-2">
                {fmtDayHeader(day)}
              </div>
              <div className="ag-card p-0 divide-y divide-[var(--ag-border)]">
                {items.map((a) => (
                  <AgendaCard
                    key={a.id}
                    appt={a}
                    isMine={a.operario_id === user?.id}
                    updating={updating === a.id}
                    onNotifyReady={() => notifyReady(a.id)}
                    onCancel={() => cancelAppt(a.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function AgendaCard({ appt, isMine, updating, onNotifyReady, onCancel }) {
  const status = appt.status || "Pendiente";
  const canNotify = status === "Pendiente" || status === "En curso";
  const canCancel = status !== "Completada" && status !== "Cancelado" && status !== "Ready";

  return (
    <div className="p-4" data-testid={`empleado-agenda-card-${appt.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <CarSimple size={16} weight="duotone" className="text-[var(--ag-gold)] shrink-0" />
            <span className="font-mono-tab font-bold text-sm">{appt.license_plate || "—"}</span>
            <span className="text-xs text-[var(--ag-text-dim)]">·</span>
            <span className="text-sm text-[var(--ag-text)]">{appt.wash_type}</span>
            {isMine && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] rounded">
                Tuya
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[var(--ag-text-dim)] mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Clock size={11} />
              <span className="tabular-nums">{fmtDateTime(appt.date)}</span>
            </span>
            {appt.customer_name && (
              <span className="inline-flex items-center gap-1">
                <UserIcon size={11} />
                {appt.customer_name}
              </span>
            )}
          </div>
          {appt.notes && (
            <div className="text-[11px] text-[var(--ag-text-dim)] mt-1.5 italic">
              “{appt.notes}”
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
            status === "Pendiente" ? "bg-amber-500/15 text-amber-300"
            : status === "En curso" ? "bg-[var(--ag-gold)]/20 text-[var(--ag-gold)]"
            : status === "Completada" || status === "Ready" ? "bg-emerald-500/15 text-emerald-300"
            : "bg-[var(--ag-midnight)] text-[var(--ag-text-dim)]"
          }`}>
            {status}
          </span>
        </div>
      </div>

      {/* Acciones · Founder Decision #2: empleado puede notificar y cancelar */}
      {(canNotify || canCancel) && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {canNotify && (
            <Button
              variant="outline"
              size="sm"
              onClick={onNotifyReady}
              disabled={updating}
              className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 rounded text-xs"
              data-testid={`empleado-appt-notify-${appt.id}`}
            >
              <CheckCircle size={12} weight="fill" className="mr-1" /> Avisar listo
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={updating}
              className="bg-transparent border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-red-400 hover:border-red-400/50 rounded text-xs ml-auto"
              data-testid={`empleado-appt-cancel-${appt.id}`}
            >
              <XIcon size={12} className="mr-1" /> Cancelar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
